import React, { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import type {
  CustomAuthAccountData,
  ICustomAuthPublicClientApplication,
} from "@azure/msal-browser/custom-auth";
import { Box, CircularProgress, Typography } from "@mui/material";
import { authRuntimeConfig } from "../../authConfig";
import { ResolvedAffiliate } from "../../affiliate/types";
import { applyAuthResult } from "../../auth/session";
import { readStorage, writeStorage } from "../../auth/browserStorage";
import { resetRedirectLoopGuard } from "../../auth/redirectFallback";
import { NativeAuthStep, NativeAuthView, useNativeAuth } from "../../auth/native/useNativeAuth";
import { AffiliateAuthShell } from "./components/AuthShell";
import {
  CodeForm,
  PasswordForm,
  ResetPasswordStartForm,
  SignInForm,
  SignUpForm,
} from "./components/AuthForms";

const RELOAD_GUARD_KEY = "auth.nativeReload";

const VIEW_PATHS: Record<NativeAuthView, string> = {
  signIn: "/login",
  signUp: "/signup",
  resetPassword: "/reset-password",
};

const TITLES: Record<NativeAuthStep["kind"], string> = {
  signIn: "Sign in",
  signInPassword: "Enter your password",
  signInCode: "Check your email",
  signUp: "Create your account",
  signUpCode: "Verify your email",
  signUpPassword: "Create a password",
  resetPassword: "Reset your password",
  resetCode: "Check your email",
  resetNewPassword: "Choose a new password",
  completing: "Signing you in",
};

interface NativeAuthFlowProps {
  client: ICustomAuthPublicClientApplication;
  affiliate: ResolvedAffiliate;
  initialView: NativeAuthView;
  returnTo: string;
  onRedirectRequired: (view: NativeAuthView, loginHint?: string) => void;
}

export function NativeAuthFlow({ client, affiliate, initialView, returnTo, onRedirectRequired }: NativeAuthFlowProps) {
  const { instance } = useMsal();
  const { affiliateAttributeName, apiScopes, nativeAuthScopes, termsAttributeName } = authRuntimeConfig;

  const handleCompleted = async (account: CustomAuthAccountData) => {
    console.info("[native auth] signed in; acquiring API token", { scopes: apiScopes });
    const token = await account.getAccessToken({ forceRefresh: false, scopes: apiScopes });
    console.info("[native auth] token result", {
      completed: token.isCompleted(),
      failed: token.isFailed(),
      hasData: !!token.data,
    });

    if (token.isCompleted() && token.data) {
      resetRedirectLoopGuard();

      // The native client wrote the tokens through its own cache instance; hand them to the
      // instance the app uses so the route guard sees the account.
      try {
        await instance.hydrateCache(token.data, { scopes: apiScopes, account: token.data.account });
      } catch (error) {
        console.warn("[native auth] could not hydrate the app's token cache", error);
      }

      // Set the URL before the session changes: the signed-in router mounts at the current location.
      window.history.replaceState(null, "", returnTo);
      applyAuthResult(instance, token.data, "native");

      const recognised = !!instance.getActiveAccount() && instance.getAllAccounts().length > 0;
      console.info("[native auth] session applied", {
        activeAccount: instance.getActiveAccount()?.username ?? null,
        accounts: instance.getAllAccounts().length,
        recognised,
      });

      if (!recognised) {
        // Reload once: a fresh instance reads the session straight from storage. Guarded so a
        // cache that never materialises cannot reload forever.
        if (!readStorage("session", RELOAD_GUARD_KEY)) {
          writeStorage("session", RELOAD_GUARD_KEY, "1");
          window.location.replace(returnTo);
          return;
        }
        console.error("[native auth] the app cache did not pick up the native session");
        onRedirectRequired("signIn", account.getAccount().username);
      }
      return;
    }

    // The account is signed in but a token for the API could not be issued natively (e.g. consent
    // or a Conditional Access requirement): the hosted page can satisfy it.
    console.error("[native auth] access token could not be acquired", {
      scopes: apiScopes,
      errorData: (token.error as { errorData?: unknown } | null)?.errorData,
    });
    onRedirectRequired("signIn", account.getAccount().username);
  };

  const auth = useNativeAuth({
    client,
    // Sign-in needs the OIDC scopes; the cached API token is then read back with apiScopes alone.
    scopes: nativeAuthScopes,
    initialView,
    signUpAttributes: affiliateAttributeName ? { [affiliateAttributeName]: affiliate.slug } : undefined,
    termsAttributeName,
    supportEmail: affiliate.theme.supportEmail,
    onCompleted: handleCompleted,
    onRedirectRequired,
  });

  // Keep the address bar in step with the view without a router navigation (which would remount).
  useEffect(() => {
    const path = VIEW_PATHS[auth.view];
    if (window.location.pathname !== path) {
      window.history.replaceState(window.history.state, "", `${path}${window.location.search}`);
    }
  }, [auth.view]);

  const { step, busy, error, info } = auth;
  const common = { busy, error, info };

  const renderStep = () => {
    switch (step.kind) {
      case "signIn":
        return (
          <SignInForm
            {...common}
            onSubmit={auth.startSignIn}
            onForgotPassword={() => auth.goTo("resetPassword")}
            onCreateAccount={() => auth.goTo("signUp")}
          />
        );
      case "signInPassword":
        return (
          <PasswordForm
            {...common}
            mode="current"
            submitLabel="Sign in"
            onSubmit={auth.submitPassword}
            onBack={() => auth.goTo("signIn")}
            backLabel="Use a different email"
          />
        );
      case "signInCode":
      case "signUpCode":
      case "resetCode":
        return (
          <CodeForm
            {...common}
            email={step.username}
            codeLength={step.codeLength}
            resendAvailableAt={auth.resendAvailableAt}
            onSubmit={auth.submitCode}
            onResend={auth.resendCode}
            onBack={() => auth.goTo(step.kind === "signUpCode" ? "signUp" : step.kind === "resetCode" ? "resetPassword" : "signIn")}
            backLabel="Use a different email"
          />
        );
      case "signUp":
        return (
          <SignUpForm
            {...common}
            termsUrl={affiliate.theme.termsUrl}
            collectTerms={!!termsAttributeName}
            onSubmit={auth.startSignUp}
            onSignIn={() => auth.goTo("signIn")}
          />
        );
      case "signUpPassword":
        return (
          <PasswordForm
            {...common}
            submitLabel="Create account"
            onSubmit={auth.submitPassword}
            onBack={() => auth.goTo("signUp")}
            backLabel="Start over"
          />
        );
      case "resetPassword":
        return <ResetPasswordStartForm {...common} onSubmit={auth.startResetPassword} onBack={() => auth.goTo("signIn")} />;
      case "resetNewPassword":
        return (
          <PasswordForm
            {...common}
            submitLabel="Reset password"
            onSubmit={auth.submitPassword}
            onBack={() => auth.goTo("signIn")}
            backLabel="Back to sign in"
          />
        );
      case "completing":
        return (
          <Box role="status" aria-live="polite" sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
            <CircularProgress size={24} aria-hidden="true" />
            <Typography>Signing you in…</Typography>
          </Box>
        );
    }
  };

  return (
    <AffiliateAuthShell theme={affiliate.theme} title={TITLES[step.kind]}>
      {renderStep()}
    </AffiliateAuthShell>
  );
}
